import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { signUp } from '../lib/auth'
import { upsertProfile } from '../lib/profiles'
import { notifySignup } from '../lib/telegramAlerts'
import { trackSignUp } from '../lib/adTracking'
import { useAuth } from '../context/AuthContext'
import PhoneInput, { isValidPhoneE164 } from '../components/PhoneInput'
import AddressAutocomplete from '../components/AddressAutocomplete'
import { checkContactTaken } from '../lib/contactMatch'

// Spec §2 — field order:
//   1. First name / Last name
//   2. Checkbox "I'm staying in a hotel" → reveals Hotel name
//   3. Street address
//   4. Apt or suite number (relabeled "Room number" when hotel is on)
//   5. City / State / ZIP  (ZIP pre-filled from /entry)
//   6. Email
//   7. Phone (international)
//   8. Password
//
// Every field is required except hotel_name and room_number.
// Unchecking the hotel box clears hotel_name — no phantom hotels.

const PENDING_KEY = 'ghost-pending-signup'

export default function Signup() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const { refresh } = useAuth()

  const prefillZip = params.get('zip') || ''
  const prefillCity = params.get('city') || ''

  const [isHotel, setIsHotel] = useState(false)
  const [f, setF] = useState(() => ({
    firstName: '',
    lastName: '',
    hotelName: '',
    street: '',
    apt: '',
    city: prefillCity,
    state: prefillZip ? 'FL' : '',
    zip: prefillZip,
    email: '',
    phone: '',
    password: '',
  }))
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [confirmSent, setConfirmSent] = useState(false)
  // 'email' | 'phone' | 'both' when this email or phone already belongs to
  // an account. Shows the "you already have an account" screen instead of
  // creating a second profile.
  const [alreadyHave, setAlreadyHave] = useState('')
  // Autocomplete conflict note (message + warn flag). Cleared after 6s.
  const [autoNote, setAutoNote] = useState({ text: '', warn: false })
  const set = (k, v) => setF((cur) => ({ ...cur, [k]: v }))

  useEffect(() => {
    if (!autoNote.text) return
    const t = setTimeout(() => setAutoNote({ text: '', warn: false }), 6000)
    return () => clearTimeout(t)
  }, [autoNote])

  // Picking a hotel from the hotel field OVERWRITES the address — but we
  // tell the user (spec §3).
  function handleHotelPick(p) {
    const prevStreet = f.street.trim()
    setF((cur) => ({
      ...cur,
      hotelName: p.hotel,
      street: p.street,
      city: p.city,
      state: p.state,
      zip: p.zip,
    }))
    if (!isHotel) setIsHotel(true)
    setAutoNote({
      text: prevStreet && prevStreet !== p.street
        ? `Address replaced by the one on file for ${p.hotel}.`
        : `Address filled in from ${p.hotel}.`,
      warn: false,
    })
  }

  // Picking an address suggestion never replaces an already-typed hotel
  // name. Two branches carry the conflict logic from the mockup.
  function handleAddressPick(p) {
    setF((cur) => {
      const next = {
        ...cur,
        street: p.street,
        city: p.city,
        state: p.state,
        zip: p.zip,
      }
      if (p.hotel && isHotel && !cur.hotelName.trim()) {
        // The address is a hotel and the hotel field is empty — fill it.
        next.hotelName = p.hotel
        setAutoNote({ text: `${p.hotel} added — clear it if you're not there.`, warn: false })
      } else if (p.hotel && cur.hotelName.trim() && cur.hotelName.trim() !== p.hotel) {
        // The address belongs to a hotel the user isn't there for.
        setAutoNote({
          text: `Heads up: you wrote "${cur.hotelName.trim()}" but this address is ${p.hotel}.`,
          warn: true,
        })
      } else {
        setAutoNote({ text: '', warn: false })
      }
      return next
    })
  }

  // Uncheck hotel → wipe the hotel name so no residue sneaks into the profile.
  useEffect(() => {
    if (!isHotel && f.hotelName) set('hotelName', '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHotel])

  const secondLineLabel = useMemo(
    () => (isHotel ? 'Room number' : 'Apt or suite number'),
    [isHotel],
  )

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')
    if (!isValidPhoneE164(f.phone)) {
      setError('Please enter a valid phone number with your country code.')
      return
    }
    setSubmitting(true)

    // Before creating anything: does this email or phone already belong to
    // an account? Supabase Auth already rejects a duplicate email, but
    // nothing stopped a second account on the same phone — which is how
    // Monti ended up paying two memberships. Fails open on error.
    const taken = await checkContactTaken({ email: f.email, phone: f.phone })
    if (taken.emailTaken || taken.phoneTaken) {
      setAlreadyHave(
        taken.emailTaken && taken.phoneTaken ? 'both' : taken.emailTaken ? 'email' : 'phone',
      )
      setSubmitting(false)
      return
    }

    // Persist what the user typed BEFORE calling signUp — the
    // AuthContext bootstrap will flush this into profiles on the first
    // authenticated load, even if a magic session isn't returned here.
    const pending = {
      email: f.email,
      first_name: f.firstName,
      last_name: f.lastName,
      phone: f.phone,
      address_line1: f.street,
      address_line2: f.apt,
      city: f.city,
      state: f.state,
      zip: f.zip,
      hotel_name: isHotel ? f.hotelName : '',
      room_number: isHotel ? f.apt : '',
      is_member: false,
    }
    try { localStorage.setItem(PENDING_KEY, JSON.stringify(pending)) } catch { /* ignore */ }

    try {
      const data = await signUp({
        email: f.email,
        password: f.password,
        firstName: f.firstName,
        lastName: f.lastName,
        phone: f.phone,
        address_line1: f.street,
        address_line2: f.apt,
        city: f.city,
        state: f.state,
        zip: f.zip,
        hotel_name: isHotel ? f.hotelName : '',
        room_number: isHotel ? f.apt : '',
      })

      const userId = data?.user?.id
      const hasSession = !!data?.session
      if (userId && hasSession) {
        // Confirmation emails are OFF (spec §2). We land here almost
        // always: seed the profiles row and go straight to /order.
        await upsertProfile(userId, pending)
        // Telegram alert 1 — fire-and-forget, after the profile row exists
        // so the server can re-read it. Never blocks the redirect.
        notifySignup(userId)
        // Ad platforms: a real account was created. Fired before navigate()
        // because both pixels post synchronously from this page.
        trackSignUp()
        try { localStorage.removeItem(PENDING_KEY) } catch { /* ignore */ }
        await refresh()
        navigate('/order')
      } else {
        // Fallback path if confirmations get re-enabled — never break.
        setConfirmSent(true)
        setSubmitting(false)
      }
    } catch (err) {
      setError(err.message || 'Sign-up failed. Try again.')
      setSubmitting(false)
    }
  }

  // "You already have an account." Deliberately vague about WHICH account:
  // naming the other email — even masked as tyh***@gmail.com — would tell
  // whoever is at this screen that the address exists and who owns it. It
  // is not a dead end either: Back returns to the form so they can use a
  // different email or number.
  if (alreadyHave) {
    const what = alreadyHave === 'email'
      ? 'this email address'
      : alreadyHave === 'phone'
        ? 'this phone number'
        : 'this email address and phone number'
    return (
      <div className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center px-6 text-center">
        <div
          className="mb-4 flex h-16 w-16 items-center justify-center rounded-full text-3xl"
          style={{ background: '#FEF9E7', color: '#B58A0F' }}
        >
          👻
        </div>
        <h1 className="font-bricolage text-[22px] font-extrabold text-navy">
          It looks like you already have an account with us.
        </h1>
        <p className="mt-2 max-w-xs text-[13px] text-ink-2">
          An account is already registered with {what}.
        </p>
        <button type="button" onClick={() => navigate('/login')} className="btn-navy mt-6">
          Sign in instead →
        </button>
        <button
          type="button"
          onClick={() => navigate('/login?forgot=1')}
          className="mt-3 text-[12px] font-bold text-aqua"
        >
          Forgot your password →
        </button>
        <button
          type="button"
          onClick={() => setAlreadyHave('')}
          className="mt-6 text-[11px] font-bold text-ink-3"
        >
          ← Back
        </button>
      </div>
    )
  }

  if (confirmSent) {
    return (
      <div className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center px-6 text-center">
        <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green text-3xl text-white">
          ✉️
        </div>
        <h1 className="font-bricolage text-[22px] font-extrabold text-navy">Check your email</h1>
        <p className="mt-2 max-w-xs text-[13px] text-ink-2">
          We sent a confirmation link to <b className="text-navy">{f.email}</b>.
          Click it to activate your account, then log in.
        </p>
        <button type="button" onClick={() => navigate('/login')} className="mt-6 text-[12px] font-bold text-aqua">
          Back to log in
        </button>
      </div>
    )
  }

  return (
    <div className="mx-auto min-h-screen max-w-md bg-white">
      <header className="sticky top-0 z-10 flex items-center justify-between border-b border-line bg-white px-4 py-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="text-lg text-ink-2"
          aria-label="Back"
        >
          ←
        </button>
        <div className="font-bricolage text-[15px] font-extrabold text-navy">Create account</div>
        <div className="w-6" />
      </header>

      <div className="px-5 py-6">
        <h1 className="font-bricolage text-[20px] font-extrabold text-navy">
          Welcome to Ghost Laundry 👻
        </h1>
        <p className="mt-1 text-[13px] text-ink-2">
          Fill this once — next pickups take two taps.
        </p>

        <form onSubmit={handleSubmit} className="mt-6 space-y-5">
          {/* 1. Name */}
          <div>
            <div className="field-label">Your name</div>
            <div className="grid grid-cols-2 gap-2">
              <input
                type="text" required autoComplete="given-name"
                placeholder="First name" className="field-input"
                value={f.firstName} onChange={(e) => set('firstName', e.target.value)}
              />
              <input
                type="text" required autoComplete="family-name"
                placeholder="Last name" className="field-input"
                value={f.lastName} onChange={(e) => set('lastName', e.target.value)}
              />
            </div>
          </div>

          {/* 2. Address block */}
          <div>
            <div className="field-label">Pickup address</div>
            <label className="mb-2 flex items-center gap-2 text-[12px] font-bold text-navy">
              <input
                type="checkbox"
                checked={isHotel}
                onChange={(e) => setIsHotel(e.target.checked)}
                className="h-4 w-4 rounded border-line accent-navy"
              />
              I&apos;m staying in a hotel
            </label>
            {isHotel && (
              <div className="mb-2">
                <AddressAutocomplete
                  id="hotelName"
                  value={f.hotelName}
                  onChange={(v) => set('hotelName', v)}
                  onPick={handleHotelPick}
                  kind="hotel"
                  placeholder="Hotel name"
                />
              </div>
            )}
            <AddressAutocomplete
              id="street"
              value={f.street}
              onChange={(v) => set('street', v)}
              onPick={handleAddressPick}
              kind="address"
              placeholder="Street address"
              autoComplete="street-address"
              required
            />
            {autoNote.text && (
              <p
                className={`mt-1 text-[11px] leading-relaxed ${autoNote.warn ? 'text-[#7A5E0C]' : 'text-ink-2'}`}
                style={autoNote.warn ? { background: '#FEF6DF', border: '1px solid #F0DCA0', padding: '6px 10px', borderRadius: 8 } : undefined}
              >
                {autoNote.text}
              </p>
            )}
            <input
              type="text" autoComplete="address-line2"
              placeholder={secondLineLabel}
              className="field-input mt-2"
              value={f.apt} onChange={(e) => set('apt', e.target.value)}
            />
            <div className="mt-2 grid grid-cols-2 gap-2">
              <input
                type="text" required autoComplete="address-level2"
                placeholder="City" className="field-input"
                value={f.city} onChange={(e) => set('city', e.target.value)}
              />
              <input
                type="text" required autoComplete="address-level1"
                placeholder="State" className="field-input"
                value={f.state} onChange={(e) => set('state', e.target.value)}
              />
            </div>
            <input
              type="text" required inputMode="numeric" maxLength={5} autoComplete="postal-code"
              placeholder="ZIP" className="field-input mt-2"
              value={f.zip} onChange={(e) => set('zip', e.target.value.replace(/\D/g, '').slice(0, 5))}
            />
          </div>

          {/* 6. Email */}
          <div>
            <div className="field-label">Email</div>
            <input
              type="email" required autoComplete="email"
              placeholder="you@example.com" className="field-input"
              value={f.email} onChange={(e) => set('email', e.target.value)}
            />
          </div>

          {/* 7. Phone */}
          <div>
            <div className="field-label">Phone / WhatsApp</div>
            <PhoneInput
              value={f.phone}
              onChange={(v) => set('phone', v)}
              placeholder="Phone / WhatsApp"
              required
            />
          </div>

          {/* 8. Password */}
          <div>
            <div className="field-label">Password</div>
            <input
              type="password" required minLength={8} autoComplete="new-password"
              placeholder="At least 8 characters" className="field-input"
              value={f.password} onChange={(e) => set('password', e.target.value)}
            />
          </div>

          {error && (
            <p className="rounded-lg bg-red/10 px-3 py-2 text-[12px] font-bold text-red">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className={`btn-navy mt-4 ${submitting ? 'opacity-60' : ''}`}
          >
            {submitting ? 'Creating account…' : 'Create account'}
          </button>
          <p className="text-center text-[11px] text-ink-3">
            By continuing you agree to Terms &amp; Privacy
          </p>
        </form>
      </div>
    </div>
  )
}
